import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Button,
} from "reactstrap";
import {
  Card,
  Select,
  InputNumber,
  Button as AntButton,
  Table,
  Space,
  Typography,
  Form,
  Popconfirm,
  message,
  Empty,
  Tag,
  Alert,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import FormTextArea from "../../form-components/TextAreaComponent";
import { FormProvider, useForm } from "react-hook-form";
import { Upload } from "antd";
import type { UploadProps } from "antd";
import { Icon } from "../../../components/Component";
import Swal from "sweetalert2";
import { nanoid } from "nanoid";

const { Text } = Typography;
const { Option } = Select;

interface SelectOptions {
  label: string;
  value: string;
  uom?: string;
  category?: string;
  description?: string;
}

interface AllocatedMaterial {
  id: string;
  materialId: string;
  materialName: string;
  quantity: number;
  uom: string;
  category?: string;
  notes?: string;
}

interface WorkOrderLineModalProps {
  modal: boolean;
  closeModal: () => void;
  append: (data: any) => void;
  update: (index: any, data: any) => void;
  formData: any;
  workLevel: string;
  materialBoqId: string;
}

const WorkOrderLineModal: React.FC<WorkOrderLineModalProps> = ({
  modal,
  closeModal,
  append,
  update,
  formData,
  workLevel,
  materialBoqId,
}) => {
  const [workCategoryOptions, setWorkCategoryOptions] = useState<
    SelectOptions[]
  >([]);
  const [workSubCategoryOptions, setWorkSubCategoryOptions] = useState<
    SelectOptions[]
  >([]);
  const [uomOptions, setUomOptions] = useState<SelectOptions[]>([]);
  const [materialOptions, setMaterialOptions] = useState<SelectOptions[]>([]);
  const [towerVillaOptions, setTowerVillaOptions] = useState<SelectOptions[]>(
    []
  );
  const [uploadedDocs, setUploadedDocs] = useState<any[]>([]);

  // Material allocation state
  const [allocatedMaterials, setAllocatedMaterials] = useState<
    AllocatedMaterial[]
  >([]);
  const [selectedMaterialId, setSelectedMaterialId] = useState<string>("");
  const [materialQuantity, setMaterialQuantity] = useState<number>(1);
  const [editingMaterialId, setEditingMaterialId] = useState<string | null>(
    null
  );
  const [editingQuantity, setEditingQuantity] = useState<number>(0);

  const methods = useForm({
    mode: "onChange",
  });

  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = methods;

  const workCategoryWatch = watch("workCategory");

  useEffect(() => {
    fetchWorkCategories();
    fetchUomOptions();
    fetchMaterialOptions();
    fetchTowerVillaOptions();
  }, []);

  useEffect(() => {
    if (workCategoryWatch) {
      fetchWorkSubCategories(workCategoryWatch);
    }
  }, [workCategoryWatch]);

  useEffect(() => {
    if (modal && formData?.item) {
      // Edit mode - populate form with existing data
      const item = formData.item;
      setValue("workCategory", item.workCategory || "");
      setValue("workSubCategory", item.workSubCategory || "");
      setValue("taskTitle", item.taskTitle || "");
      setValue("taskDescription", item.taskDescription || "");
      setValue("quantity", item.quantity || 0);
      setValue("uom", item.uom || "");
      setValue("towerVilla", item.towerVilla || "");
      setValue("floorName", item.floorName || "");
      setValue("flatName", item.flatName || "");
      setUploadedDocs(item.uploadedDocs || []);
      setAllocatedMaterials(item.allocatedMaterials || []);
    } else {
      // Add mode - reset form
      reset();
      setUploadedDocs([]);
      setAllocatedMaterials([]);
      setSelectedMaterialId("");
      setMaterialQuantity(1);
    }
  }, [modal, formData, setValue, reset]);

  const fetchWorkCategories = async () => {
    try {
      const mockCategories = [
        { label: "Electrical", value: "Electrical" },
        { label: "Plumbing", value: "Plumbing" },
        { label: "Civil", value: "Civil" },
        { label: "Mechanical", value: "Mechanical" },
        { label: "HVAC", value: "HVAC" },
        { label: "Painting", value: "Painting" },
        { label: "Flooring", value: "Flooring" },
        { label: "Carpentry", value: "Carpentry" },
        { label: "Masonry", value: "Masonry" },
        { label: "Waterproofing", value: "Waterproofing" },
      ];
      setWorkCategoryOptions(mockCategories);
    } catch (error) {
      console.log("Error fetching work categories:", error);
    }
  };

  const fetchWorkSubCategories = async (category: string) => {
    try {
      const subCategoryMap: { [key: string]: SelectOptions[] } = {
        Electrical: [
          { label: "Wiring", value: "Wiring" },
          { label: "Lighting", value: "Lighting" },
          { label: "Power", value: "Power" },
          { label: "Cabling", value: "Cabling" },
          { label: "Panel Installation", value: "Panel Installation" },
          { label: "Switch Installation", value: "Switch Installation" },
        ],
        Plumbing: [
          { label: "Water Supply", value: "Water Supply" },
          { label: "Drainage", value: "Drainage" },
          { label: "Sanitary", value: "Sanitary" },
          { label: "Pipe Installation", value: "Pipe Installation" },
          { label: "Fixture Installation", value: "Fixture Installation" },
        ],
        Civil: [
          { label: "Concrete Work", value: "Concrete Work" },
          { label: "Masonry", value: "Masonry" },
          { label: "Structural", value: "Structural" },
          { label: "Foundation", value: "Foundation" },
          { label: "Plastering", value: "Plastering" },
        ],
        Mechanical: [
          { label: "Equipment Installation", value: "Equipment Installation" },
          { label: "Machinery Setup", value: "Machinery Setup" },
          { label: "Maintenance", value: "Maintenance" },
        ],
        HVAC: [
          { label: "Duct Installation", value: "Duct Installation" },
          { label: "AC Installation", value: "AC Installation" },
          { label: "Ventilation", value: "Ventilation" },
        ],
        Painting: [
          { label: "Interior Painting", value: "Interior Painting" },
          { label: "Exterior Painting", value: "Exterior Painting" },
          { label: "Primer Application", value: "Primer Application" },
        ],
        Flooring: [
          { label: "Tile Installation", value: "Tile Installation" },
          { label: "Wood Flooring", value: "Wood Flooring" },
          { label: "Marble Installation", value: "Marble Installation" },
        ],
      };
      setWorkSubCategoryOptions(subCategoryMap[category] || []);
    } catch (error) {
      console.log("Error fetching work sub-categories:", error);
    }
  };

  const fetchUomOptions = async () => {
    try {
      const mockUoms = [
        { label: "Meter (m)", value: "m" },
        { label: "Square Meter (sqm)", value: "sqm" },
        { label: "Cubic Meter (cum)", value: "cum" },
        { label: "Number (nos)", value: "nos" },
        { label: "Kilogram (kg)", value: "kg" },
        { label: "Set", value: "set" },
        { label: "Point", value: "point" },
        { label: "Running Meter (rm)", value: "rm" },
        { label: "Square Feet (sft)", value: "sft" },
        { label: "Feet (ft)", value: "ft" },
      ];
      setUomOptions(mockUoms);
    } catch (error) {
      console.log("Error fetching UOM options:", error);
    }
  };

  const fetchMaterialOptions = async () => {
    try {
      const mockMaterials = [
        {
          label: "Steel Bars 12mm",
          value: "steel_bars_12mm",
          uom: "kg",
          category: "Steel",
          description: "High grade steel bars",
        },
        {
          label: "Cement Bags (50kg)",
          value: "cement_bags",
          uom: "bags",
          category: "Cement",
          description: "Premium cement bags",
        },
        {
          label: "Sand (River)",
          value: "sand_river",
          uom: "cum",
          category: "Aggregates",
          description: "Fine river sand",
        },
        {
          label: "Electrical Wire 2.5mm",
          value: "wire_2_5mm",
          uom: "m",
          category: "Electrical",
          description: "Copper electrical wire",
        },
        {
          label: "PVC Pipes 4 inch",
          value: "pvc_pipes_4_inch",
          uom: "m",
          category: "Plumbing",
          description: "PVC pipes for plumbing",
        },
        {
          label: "Paint (Interior) - White",
          value: "paint_interior_white",
          uom: "ltrs",
          category: "Paint",
          description: "Interior wall paint",
        },
        {
          label: "Tiles (Ceramic) 2x2",
          value: "tiles_ceramic_2x2",
          uom: "sqm",
          category: "Tiles",
          description: "Ceramic floor tiles",
        },
        {
          label: "Switches (Modular)",
          value: "switches_modular",
          uom: "nos",
          category: "Electrical",
          description: "Modular electrical switches",
        },
        {
          label: "LED Bulbs 9W",
          value: "led_bulbs_9w",
          uom: "nos",
          category: "Electrical",
          description: "Energy efficient LED bulbs",
        },
        {
          label: "Concrete Mix M25",
          value: "concrete_m25",
          uom: "cum",
          category: "Concrete",
          description: "Ready mix concrete M25 grade",
        },
      ];
      setMaterialOptions(mockMaterials);
    } catch (error) {
      console.log("Error fetching material options:", error);
    }
  };

  const fetchTowerVillaOptions = async () => {
    try {
      const mockOptions = [
        { label: "Tower A", value: "tower_a" },
        { label: "Tower B", value: "tower_b" },
        { label: "Tower C", value: "tower_c" },
        { label: "Villa Block 1", value: "villa_block_1" },
        { label: "Villa Block 2", value: "villa_block_2" },
        { label: "Villa Block 3", value: "villa_block_3" },
      ];
      setTowerVillaOptions(mockOptions);
    } catch (error) {
      console.log("Error fetching tower/villa options:", error);
    }
  };

  // Material allocation methods
  const getAvailableMaterials = () => {
    const allocatedIds = allocatedMaterials.map(
      (material) => material.materialId
    );
    return materialOptions.filter(
      (option) => !allocatedIds.includes(option.value)
    );
  };

  const handleAddMaterial = () => {
    if (!selectedMaterialId) {
      message.warning("Please select a material first");
      return;
    }

    if (!materialQuantity || materialQuantity <= 0) {
      message.error("Quantity must be greater than 0");
      return;
    }

    if (
      allocatedMaterials.some(
        (material) => material.materialId === selectedMaterialId
      )
    ) {
      message.error("This material has already been allocated");
      return;
    }

    const materialToAdd = materialOptions.find(
      (material) => material.value === selectedMaterialId
    );

    if (!materialToAdd) {
      message.error("Selected material not found");
      return;
    }

    const newAllocatedMaterial: AllocatedMaterial = {
      id: nanoid(),
      materialId: materialToAdd.value,
      materialName: materialToAdd.label,
      quantity: materialQuantity,
      uom: materialToAdd.uom || "nos",
      category: materialToAdd.category,
    };

    const updatedMaterials = [...allocatedMaterials, newAllocatedMaterial];
    setAllocatedMaterials(updatedMaterials);

    setSelectedMaterialId("");
    setMaterialQuantity(1);

    message.success(`${materialToAdd.label} added successfully`);
  };

  const handleRemoveMaterial = (materialId: string) => {
    const materialToRemove = allocatedMaterials.find(
      (m) => m.id === materialId
    );
    const updatedMaterials = allocatedMaterials.filter(
      (material) => material.id !== materialId
    );

    setAllocatedMaterials(updatedMaterials);
    message.success(`${materialToRemove?.materialName} removed successfully`);
  };

  const handleStartEdit = (materialId: string, currentQuantity: number) => {
    setEditingMaterialId(materialId);
    setEditingQuantity(currentQuantity);
  };

  const handleSaveEdit = (materialId: string) => {
    if (editingQuantity <= 0) {
      message.error("Quantity must be greater than 0");
      return;
    }

    const updatedMaterials = allocatedMaterials.map((material) =>
      material.id === materialId
        ? { ...material, quantity: editingQuantity }
        : material
    );

    setAllocatedMaterials(updatedMaterials);
    setEditingMaterialId(null);
    message.success("Quantity updated successfully");
  };

  const handleCancelEdit = () => {
    setEditingMaterialId(null);
    setEditingQuantity(0);
  };

  // Table columns for material allocation
  const materialColumns: ColumnsType<AllocatedMaterial> = [
    {
      title: "Material Name",
      dataIndex: "materialName",
      key: "materialName",
      width: "40%",
      render: (name: string, record: AllocatedMaterial) => (
        <div>
          <Text strong>{name}</Text>
          {record.category && (
            <div style={{ marginTop: 4 }}>
              <Tag color="blue">{record.category}</Tag>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: "25%",
      render: (quantity: number, record: AllocatedMaterial) => {
        const isEditing = editingMaterialId === record.id;

        return isEditing ? (
          <Space>
            <InputNumber
              min={0.01}
              step={0.01}
              value={editingQuantity}
              onChange={(value) => setEditingQuantity(value || 0)}
              style={{ width: "80px" }}
              size="small"
            />
            <AntButton
              type="text"
              icon={<CheckOutlined />}
              onClick={() => handleSaveEdit(record.id)}
              size="small"
              style={{ color: "#52c41a" }}
            />
            <AntButton
              type="text"
              icon={<CloseOutlined />}
              onClick={handleCancelEdit}
              size="small"
              style={{ color: "#ff4d4f" }}
            />
          </Space>
        ) : (
          <Space>
            <Text>{quantity}</Text>
            <AntButton
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleStartEdit(record.id, quantity)}
              size="small"
            />
          </Space>
        );
      },
    },
    {
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
      width: "15%",
      render: (uom: string) => <Tag color="geekblue">{uom}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: "20%",
      render: (_, record: AllocatedMaterial) => (
        <Popconfirm
          title="Remove Material"
          description={`Are you sure you want to remove ${record.materialName}?`}
          onConfirm={() => handleRemoveMaterial(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <AntButton type="text" danger icon={<DeleteOutlined />} size="small">
            Remove
          </AntButton>
        </Popconfirm>
      ),
    },
  ];

  const uploadProps: UploadProps = {
    name: "file",
    multiple: true,
    accept: ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png",
    beforeUpload: (file) => {
      const isValidType = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "image/jpeg",
        "image/png",
        "image/jpg",
      ].includes(file.type);

      if (!isValidType) {
        Swal.fire({
          icon: "error",
          title: "Invalid File Type",
          text: "Only PDF, DOC, XLS, and Image files are allowed",
        });
        return false;
      }

      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        Swal.fire({
          icon: "error",
          title: "File Too Large",
          text: "File must be smaller than 10MB",
        });
        return false;
      }

      return false;
    },
    onChange: (info) => {
      setUploadedDocs(info.fileList);
    },
    fileList: uploadedDocs,
  };

  const validateForm = (data: any) => {
    if (!data.workCategory?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Work Category is required",
      });
      return false;
    }

    if (!data.taskTitle?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Task Title is required",
      });
      return false;
    }

    if (data.taskTitle.length > 150) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Task Title cannot exceed 150 characters",
      });
      return false;
    }

    if (!data.taskDescription?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Task Description is required",
      });
      return false;
    }

    if (data.taskDescription.length > 500) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Task Description cannot exceed 500 characters",
      });
      return false;
    }

    if (!data.quantity || data.quantity <= 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Quantity must be greater than 0",
      });
      return false;
    }

    if (!data.uom) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Unit of Measurement is required",
      });
      return false;
    }

    // Validate allocated materials
    for (const material of allocatedMaterials) {
      if (!material.quantity || material.quantity <= 0) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: `Material "${material.materialName}" must have quantity greater than 0`,
        });
        return false;
      }
    }

    // Check location fields based on work level
    if (["Tower", "Villa"].includes(workLevel)) {
      if (!data.towerVilla) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Tower/Villa selection is required for selected work level",
        });
        return false;
      }
    }

    if (workLevel === "Floor" && !data.floorName?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Floor Name is required for Floor level work",
      });
      return false;
    }

    if (workLevel === "Flat" && !data.flatName?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Flat Name is required for Flat level work",
      });
      return false;
    }

    return true;
  };

  const onSubmit = (data: any) => {
    if (!validateForm(data)) return;

    const lineData = {
      id: formData?.item?.id || nanoid(),
      workCategory: data.workCategory,
      workSubCategory: data.workSubCategory,
      taskTitle: data.taskTitle,
      taskDescription: data.taskDescription,
      quantity: Number(data.quantity),
      uom: data.uom,
      allocatedMaterials: allocatedMaterials,
      towerVilla: data.towerVilla,
      floorName: data.floorName,
      flatName: data.flatName,
      uploadedDocs: uploadedDocs,
    };

    if (formData.action === "edit" && formData.index !== null) {
      update(formData.index, lineData);
    } else {
      append(lineData);
    }

    closeModal();

    Swal.fire({
      icon: "success",
      title: "Success",
      text: `Work order line ${
        formData.action === "edit" ? "updated" : "added"
      } successfully`,
      timer: 1500,
      showConfirmButton: false,
    });
  };

  const availableForSelection = getAvailableMaterials();

  return (
    <Modal
      isOpen={modal}
      toggle={closeModal}
      size="xl"
      className="modal-dialog-centered"
    >
      <ModalHeader toggle={closeModal}>
        {formData.action === "edit"
          ? "Edit Work Order Line"
          : "Add Work Order Line"}
      </ModalHeader>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <Row className="gy-3">
              <Col lg="6">
                <FormSelect
                  name="workCategory"
                  label="Work Category"
                  options={workCategoryOptions}
                  required={true}
                  placeholder="Select Work Category"
                  tooltipText="Major category of work (e.g., Electrical, Plumbing)"
                />
              </Col>
              <Col lg="6">
                <FormSelect
                  name="workSubCategory"
                  label="Work Sub Category"
                  options={workSubCategoryOptions}
                  placeholder="Select Sub Category (Optional)"
                  tooltipText="Subdivision under selected category"
                />
              </Col>

              <Col lg="12">
                <FormInput
                  name="taskTitle"
                  label="Task Title"
                  required={true}
                  placeholder="Enter task title (Max 150 characters)"
                  maxLength={150}
                  tooltipText="Title or summary of task"
                />
              </Col>

              <Col lg="12">
                <FormTextArea
                  name="taskDescription"
                  label="Task Description"
                  required={true}
                  placeholder="Enter detailed task description (Max 500 characters)"
                  maxLength={500}
                  rows={3}
                />
              </Col>

              <Col lg="6">
                <FormInput
                  name="quantity"
                  label="Quantity"
                  type="number"
                  required={true}
                  placeholder="Enter quantity"
                  min={0.01}
                  tooltipText="Estimated quantity of the task"
                />
              </Col>
              <Col lg="6">
                <FormSelect
                  name="uom"
                  label="Unit of Measurement"
                  options={uomOptions}
                  required={true}
                  placeholder="Select UOM"
                  tooltipText="Unit of Measurement"
                />
              </Col>

              {["Tower", "Villa"].includes(workLevel) && (
                <Col lg="6">
                  <FormSelect
                    name="towerVilla"
                    label="Tower/Villa Selection"
                    options={towerVillaOptions}
                    required={true}
                    placeholder="Select Tower/Villa"
                    tooltipText="Based on selected segment level"
                  />
                </Col>
              )}

              {workLevel === "Floor" && (
                <Col lg="6">
                  <FormInput
                    name="floorName"
                    label="Floor Name"
                    required={true}
                    placeholder="Enter floor name"
                    tooltipText="Name of the floor if applicable"
                  />
                </Col>
              )}

              {workLevel === "Flat" && (
                <Col lg="6">
                  <FormInput
                    name="flatName"
                    label="Flat Name"
                    required={true}
                    placeholder="Enter flat name"
                    tooltipText="Name of flat/unit if applicable"
                  />
                </Col>
              )}

              {/* Enhanced Material Allocation Section */}
              <Col lg="12">
                <div style={{ marginBottom: "16px" }}>
                  <label className="form-label d-flex align-items-center">
                    Material Allocation (Optional)
                  </label>
                  <small className="text-muted">
                    Select materials from the linked Material BOQ
                  </small>
                </div>

                {/* Add Material Section */}
                <Card
                  size="small"
                  title="Add New Material"
                  style={{ marginBottom: "16px" }}
                  bodyStyle={{ padding: "16px" }}
                >
                  <Row gutter={16} align="bottom">
                    <Col xs={24} sm={6} md={6}>
                      <label className="form-label mb-1">Select Material</label>
                      <Select
                        placeholder="Choose material to allocate"
                        value={selectedMaterialId || undefined}
                        onChange={setSelectedMaterialId}
                        showSearch
                        filterOption={(input, option) =>
                          option?.children
                            ?.toString()
                            .toLowerCase()
                            .includes(input.toLowerCase()) || false
                        }
                        disabled={availableForSelection.length === 0}
                        style={{ width: "100%" }}
                      >
                        {availableForSelection.map((material) => (
                          <Option key={material.value} value={material.value}>
                            <div>
                              <Text>{material.label}</Text>
                              <Text
                                type="secondary"
                                style={{ marginLeft: 8, fontSize: "12px" }}
                              >
                                ({material.uom})
                              </Text>
                              {material.category && (
                                <Tag color="blue" style={{ marginLeft: 8 }}>
                                  {material.category}
                                </Tag>
                              )}
                            </div>
                          </Option>
                        ))}
                      </Select>
                    </Col>

                    <Col xs={12} sm={4} md={4}>
                      <label className="form-label  mb-1">Quantity</label>
                      <InputNumber
                        min={0.01}
                        step={0.01}
                        value={materialQuantity}
                        onChange={(value) => setMaterialQuantity(value || 1)}
                        placeholder="Enter qty"
                        style={{ width: "100%" }}
                      />
                    </Col>

                    <Col xs={12} sm={2} md={2}>
                      <AntButton
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddMaterial}
                        disabled={
                          !selectedMaterialId ||
                          !materialQuantity ||
                          materialQuantity <= 0
                        }
                        style={{ width: "100%", marginTop: "32px" }}
                      >
                        Add
                      </AntButton>
                    </Col>
                  </Row>

                  {availableForSelection.length === 0 && (
                    <Alert
                      message="No materials available"
                      description="All available materials have been allocated or no materials are available."
                      type="info"
                      showIcon
                      style={{ marginTop: 16 }}
                    />
                  )}
                </Card>

                {/* Allocated Materials Table */}
                <Card
                  size="small"
                  title={`Allocated Materials (${allocatedMaterials.length})`}
                >
                  {allocatedMaterials.length > 0 ? (
                    <Table
                      columns={materialColumns}
                      dataSource={allocatedMaterials}
                      rowKey="id"
                      pagination={false}
                      size="small"
                      scroll={{ x: 600 }}
                    />
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <span>
                          No materials allocated yet
                          <br />
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            Use the form above to add materials
                          </Text>
                        </span>
                      }
                      style={{ padding: "40px 20px" }}
                    />
                  )}
                </Card>

                {allocatedMaterials.length > 0 && (
                  <div className="mt-2">
                    <small className="text-success">
                      <Icon name="check-circle" className="me-1" />
                      {allocatedMaterials.length} material
                      {allocatedMaterials.length > 1 ? "s" : ""} allocated
                    </small>
                  </div>
                )}
              </Col>

              <Col lg="12" className="mb-3">
                <label className="form-label">
                  Upload Documents (Optional)
                </label>
                <Upload.Dragger {...uploadProps}>
                  <p className="ant-upload-drag-icon">
                    <Icon name="upload" />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag files to upload technical documents/drawings
                  </p>
                  <p className="ant-upload-hint">
                    Supports: PDF, DOC, XLS, Images (Max 10MB each)
                  </p>
                </Upload.Dragger>
              </Col>
            </Row>
            {/* <Row>
              {uploadedDocs && uploadedDocs.length > 0 && (
                <div className="mt-2">
                  <small className="text-success">
                    {uploadedDocs.length} file(s) selected
                  </small>
                </div>
              )}
            </Row> */}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button color="primary" type="submit">
              {formData.action === "edit" ? "Update Line" : "Add Line"}
            </Button>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default WorkOrderLineModal;
